import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Truck, AlertTriangle, ExternalLink, Loader2 } from "lucide-react";
import { logisticsApi } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import GlobalLogisticsTodoDialog from "@/components/orders/GlobalLogisticsTodoDialog";

const STATUS_LABEL = {
  todo: "À faire",
  requested: "Demandé",
  accepted: "Accepté",
  in_delivery: "En livraison",
  delivered: "Livré",
};

const STATUS_BADGE = {
  todo: "bg-orange-100 text-orange-800",
  requested: "bg-slate-200 text-slate-800",
  accepted: "bg-blue-100 text-blue-800",
  in_delivery: "bg-amber-100 text-amber-800",
  delivered: "bg-emerald-100 text-emerald-800",
};

/**
 * Widget Dashboard : Éléments logistiques.
 * Liste compacte de tous les besoins logistiques (cross-commandes) avec
 * statut + commande, et alerte des items en retard de livraison.
 * Le clic sur l'entête ou un item ouvre la modale globale "À traiter et commander".
 */
export default function LogisticsItemsWidget() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      logisticsApi.getLogisticsTodo().then((r) => r.data?.items || []).catch(() => []),
      logisticsApi.getLogisticsOverdue().then((r) => r.data?.items || []).catch(() => []),
    ]).then(([t, ov]) => {
      setItems(t);
      setOverdue(ov);
      setLoading(false);
    });
  }, [open]); // refresh after dialog closes

  const grouped = useMemo(() => {
    const out = { todo: 0, requested: 0, accepted: 0, in_delivery: 0, delivered: 0 };
    items.forEach((i) => { out[i.status] = (out[i.status] || 0) + 1; });
    return out;
  }, [items]);

  const displayItems = useMemo(
    () => items.filter((i) => i.status !== "delivered").slice(0, 12),
    [items],
  );

  return (
    <>
      <Card data-testid="dashboard-logistics-items-widget" className="cursor-pointer hover:shadow-md transition-shadow">
        <CardHeader
          className="pb-3"
          onClick={() => setOpen(true)}
          data-testid="dashboard-logistics-open-global"
        >
          <CardTitle className="font-heading flex items-center gap-2 text-base sm:text-lg">
            <Truck className="h-5 w-5 text-blue-700" />
            Éléments logistiques
            <Badge variant="secondary" className="ml-1">{items.length}</Badge>
            {overdue.length > 0 && (
              <Badge className="bg-red-100 text-red-800 ml-1" data-testid="dashboard-logistics-overdue-badge">
                <AlertTriangle className="h-3 w-3 mr-1" />
                {overdue.length} en retard
              </Badge>
            )}
            <span className="ml-auto text-xs font-normal text-blue-700 inline-flex items-center gap-1">
              Ouvrir la vue globale <ExternalLink className="h-3 w-3" />
            </span>
          </CardTitle>
          <div className="flex flex-wrap gap-2 pt-2 text-xs">
            {Object.entries(grouped).map(([k, v]) => (
              v > 0 ? (
                <span key={k} className={`px-2 py-0.5 rounded-full ${STATUS_BADGE[k]}`} data-testid={`dashboard-logistics-stat-${k}`}>
                  {STATUS_LABEL[k]}: <b>{v}</b>
                </span>
              ) : null
            ))}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
            </div>
          ) : displayItems.length === 0 ? (
            <p className="text-sm text-muted-foreground italic py-2" data-testid="dashboard-logistics-empty">
              Aucun élément logistique en attente.
            </p>
          ) : (
            <ul className="divide-y" data-testid="dashboard-logistics-list">
              {displayItems.map((it) => {
                const isOverdue = overdue.some((o) => o.item_id === it.item_id);
                return (
                  <li
                    key={it.item_id}
                    className="flex items-center gap-2 py-2 text-sm hover:bg-slate-50 cursor-pointer px-1 -mx-1 rounded"
                    onClick={() => setOpen(true)}
                    data-testid={`dashboard-logistics-row-${it.item_id}`}
                  >
                    <span
                      className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: it.category_color || "#94a3b8" }}
                    />
                    <span className="truncate flex-1">
                      <span className="font-medium">{it.label}</span>
                      <span className="text-xs text-muted-foreground"> · {it.category_name}</span>
                    </span>
                    <span className="text-xs text-muted-foreground font-mono whitespace-nowrap hidden md:inline">
                      {it.order_reference || it.order_id?.slice(0, 6)}
                    </span>
                    {isOverdue && (
                      <Badge className="bg-red-100 text-red-800 text-[10px]" title="Livraison en retard">
                        <AlertTriangle className="h-3 w-3" />
                      </Badge>
                    )}
                    <span className={`text-[10px] px-2 py-0.5 rounded ${STATUS_BADGE[it.status]} whitespace-nowrap`}>
                      {STATUS_LABEL[it.status]}
                    </span>
                  </li>
                );
              })}
              {items.filter((i) => i.status !== "delivered").length > displayItems.length && (
                <li className="pt-2 text-xs text-blue-700 hover:underline cursor-pointer" onClick={() => setOpen(true)}>
                  + {items.filter((i) => i.status !== "delivered").length - displayItems.length} autres éléments — voir tout
                </li>
              )}
            </ul>
          )}
        </CardContent>
      </Card>

      <GlobalLogisticsTodoDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
